import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_14: GgxTourDefinition = {
    key: '[HOWTO-14] Post-Production Human Labeling',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 5,
    prerequisite: {
        target: '[test-id="home-page-card-Monitor & Track"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Where people check the machine',
        subtitle:
            'Automated metrics tell you a lot, but they cannot tell you whether an answer was ' +
            'actually right. Only a person who knows the business can. Annotation Queues are ' +
            'where those people look at real production interactions, mark what the pipeline ' +
            'got right and wrong, and write down the correct answer. That judgement fixes ' +
            "today's problems and becomes the ground truth for tomorrow's tests. In this tour " +
            'you will open one queue, see how it is set up, look at the data, see where labels ' +
            'are added, and check how far the review has come.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You opened Customer Intent Classification Annotations and walked its four tabs: ' +
            'Details, which defines what annotators are asked and which columns they see; ' +
            'Data, the raw production records with their audit trail; Labeling, where a person ' +
            'marks each prediction right or wrong and writes the correct intent; and ' +
            'Statistics, which shows how much has been reviewed and how accurate the pipeline ' +
            'looks on reviewed records. Those human labels are exactly what automated ' +
            'monitoring measures against, and the Post-Production Automated Monitoring tour ' +
            'shows how this work turns into a trend the whole team can watch.',
        learned: [
            '**Details** define what annotators are asked.',
            '**Labeling** is where a person marks each prediction right or wrong.',
            '**Statistics** show how much is reviewed and how accurate it looks.',
        ],
    },
    chapters: [
        {
            name: 'Find the queue',
            blurb: 'From the home page to one annotation queue',
            icon: 'map',
            steps: [2, 3, 4],
        },
        {
            name: 'How it is set up',
            blurb: 'What annotators are asked to fill in',
            icon: 'list',
            steps: [5],
        },
        {
            name: 'Data and labels',
            blurb: 'Raw records, then human judgement',
            icon: 'grid',
            steps: [6, 7, 8, 9, 10, 11],
        },
        {
            name: 'Progress',
            blurb: 'How much is reviewed and how accurate it is',
            icon: 'pulse',
            steps: [12, 13],
        },
    ],
    steps: [
        {
            target: '[test-id="home-page-card-Monitor & Track"] [test-id="launch-button"]',
            title: 'Where production gets reviewed',
            whatHappens:
                'the module opens on the Monitoring page, with Annotation Queues beside it in the ' +
                'sidebar.',
            icon: 'map',
            description:
                '<strong>Monitor & Track</strong> holds everything that happens after a ' +
                'pipeline goes live. Automated monitoring watches the numbers, and annotation ' +
                'queues are where people check the answers behind them. Click ' +
                '<strong>Launch</strong> on this card.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Launch',
        },
        {
            target: '[test-id="sidenav-item-tab-Annotation Queues"]',
            scrollIntoView: true,
            title: 'Open the queues',
            whatHappens: 'the Annotation Queues page opens with the queues in this project.',
            icon: 'map',
            description:
                'An <strong>annotation queue</strong> is a stream of real production ' +
                'interactions waiting for a human opinion. Teams usually keep one per pipeline ' +
                'in production, sometimes one per risk they care about. Click <strong>Annotation ' +
                'Queues</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Annotation Queues',
        },
        {
            target: '[test-id^="grid-row-link-Customer Intent Classification Annotations"]',
            targetText: 'Customer Intent Classification Annotations',
            climbToRow: true,
            scrollIntoView: true,
            waitForPresent: '[test-id^="AnnotationQueue-row-"]',
            title: 'Pick the intent classifier queue',
            whatHappens: 'the queue opens on its Details tab.',
            icon: 'search',
            description:
                'We will use <strong>Customer Intent Classification Annotations</strong>, the ' +
                'queue for the pipeline that sorts each customer message into an intent. It is ' +
                'a classification pipeline, but the same idea works for chat, summarization and ' +
                'synthetic data pipelines: people look at what went in and what came out, and ' +
                'say whether it was right. Click the row.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Customer Intent Classification Annotations',
        },
        {
            target: '[test-id="annotation-queue-feedback-columns"]',
            frame: true,
            scrollIntoView: true,
            title: 'What annotators are asked',
            whatHappens: 'next, the raw production data in this queue.',
            icon: 'list',
            description:
                'The <strong>Details</strong> tab is the setup. This block, <strong>Feedback ' +
                'Columns</strong>, is the heart of it: the fields a reviewer fills in for each ' +
                'record. Here that is <strong>Is Accurate</strong> (a simple right or wrong), ' +
                '<strong>Notes</strong> for anything worth explaining, and ' +
                '<strong>ground_truth</strong> for the intent the pipeline should have picked. ' +
                'Below it, the column mappings say which columns are the input ' +
                '(<strong>user_message</strong>) and the output (<strong>classified_' +
                'intent</strong>), and the data lake connection says where new records come ' +
                'from. Change these and the labeling screen changes to match. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="annotation-queue-tab-data"]',
            title: 'Now, the data itself',
            whatHappens: 'the Data tab opens with every record in the queue.',
            icon: 'grid',
            description:
                'Before anyone labels anything, it helps to see what is actually in the queue. ' +
                'The <strong>Data</strong> tab is the raw feed from production, plus the audit ' +
                'trail of who annotated what and when. Click <strong>Data</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Data',
        },
        {
            target: '[test-id="annotation-queue-data-grid"]',
            frame: true,
            scrollIntoView: true,
            title: 'One row, one real interaction',
            whatHappens: 'next, the tab where human judgement is added.',
            icon: 'grid',
            description:
                'Each row is one thing a customer said (<strong>user_message</strong>), the ' +
                'intent the pipeline chose for it (<strong>classified_intent</strong>) and the ' +
                'date. The last columns show whatever a reviewer has already added. If your ' +
                'observability tools capture tool calls or traces, they can appear here too, so ' +
                'reviewers have the full picture. This view is where you check the data is ' +
                'complete and current before the labeling starts. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="annotation-queue-tab-labeling"]',
            title: 'Where the human judgement goes',
            whatHappens: 'the Labeling tab opens.',
            icon: 'list',
            description:
                'Reading the data is step one. Deciding whether the pipeline got it right is ' +
                'what the <strong>Labeling</strong> tab is for. Click <strong>Labeling</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Labeling',
        },
        {
            target: '[test-id="annotation-queue-labeling-grid"]',
            frame: true,
            scrollIntoView: true,
            title: 'One decision covers many records',
            whatHappens: 'next, the control a reviewer uses to mark a row.',
            icon: 'list',
            description:
                'This is where human expertise turns predictions into something the team can ' +
                'act on. To save reviewers time, identical question-and-intent pairs are grouped ' +
                'into one row with a <strong>Count</strong>, most frequent first, so one ' +
                'decision covers many records. The columns you see come from the setup in ' +
                'Details, every change saves as you go, and new production data slots in ' +
                'underneath. The <strong>Judge</strong> tab can add an LLM as a first-pass ' +
                'reviewer so people spend their time on the hard cases. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="annotation-queue-labeling-grid"] [test-id^="annotation-queue-is-accurate-"]',
            scrollIntoView: true,
            title: 'Right, wrong, or not sure',
            whatHappens: 'next, a row the reviewer marked wrong.',
            icon: 'grid',
            description:
                'This queue uses a simple rubric: was the intent <strong>accurate</strong> or ' +
                'not. For each row the reviewer clicks the <strong>tick</strong> if the ' +
                'pipeline got it right, the <strong>cross</strong> if it got it wrong, or the ' +
                '<strong>question mark</strong> to leave it undecided for now. That is the whole ' +
                'job for most rows, which is why a reviewer can get through hundreds in a ' +
                'sitting. Other queues can use different rubrics and extra columns; that is ' +
                'decided in Details. Click Continue.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="annotation-queue-labeling-grid"] [test-id="annotation-queue-is-accurate-false"]',
            scrollIntoView: true,
            title: 'When it is wrong, say what was right',
            whatHappens: 'next, how far the review has come.',
            icon: 'grid',
            description:
                'Here is a row the reviewer marked wrong. Notice they did not stop at the cross: ' +
                'they filled in <strong>ground_truth</strong> with the intent the pipeline ' +
                'should have chosen, and left a <strong>note</strong> explaining why. That pair ' +
                'is gold for the people who maintain the pipeline, because it tells them what ' +
                'to fix in the prompt or the guardrails, not just that something was off. When ' +
                'no existing intent fits the question, the note says so, and that is how new ' +
                'intents get discovered. Click Continue.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="annotation-queue-tab-stats"]',
            title: 'How is the review going?',
            whatHappens: 'the Statistics tab opens.',
            icon: 'pulse',
            description:
                'Labels are only useful once there are enough of them. <strong>Statistics' +
                '</strong> shows how much of the queue has been reviewed and what the reviewers ' +
                'found. Click <strong>Statistics</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Statistics',
        },
        {
            target: '[test-id="card-Annotated"]',
            climbTo: '.row',
            frame: true,
            scrollIntoView: true,
            title: 'Progress and accuracy at a glance',
            whatHappens: 'the tour wraps up.',
            icon: 'pulse',
            description:
                'Four numbers tell the story: how many records are in the queue, how many are ' +
                'still <strong>Pending</strong>, how many are <strong>Annotated</strong>, and ' +
                'the <strong>Accuracy</strong> the pipeline achieved on the records people have ' +
                'checked. Below them, the same numbers are broken down by intent, so you can ' +
                'see which requests the classifier struggles with and where reviewers should ' +
                'spend their time next. The date filter at the top narrows all of it to a ' +
                'period. Click Continue to finish.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
